import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getCollection, requestJson, stripEmptyValues } from '../lib/api';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import { useMenuPage } from '../hooks/useMenuPage';

const emptyForm = {
  code: '',
  identificationNumber: '',
  externalReference: '',
  logoUrl: '',
  name: '',
  type: 'store',
  storeType: '',
  jobNumberPattern: '',
  invoiceNumberPattern: '',
  address1: '',
  address2: '',
  postalCode: '',
  city: '',
  state: '',
  country: 'France',
  phone: '',
  email: '',
  supplierAccountSupplier: 'BBGR',
  supplierAccountNumber: '',
  authPassword: '',
};

function getPrimarySupplierAccount(item) {
  const accounts = item.supplierAccounts || [];
  return accounts.find((account) => account.supplier === 'BBGR') || accounts.find((account) => account.supplier === 'ESSILOR') || accounts[0] || null;
}

function getSiteTypeLabel(type) {
  if (type === 'store') return 'Magasin';
  if (type === 'workshop') return 'Atelier';
  if (type === 'sav') return 'Logistique';
  if (type === 'direction') return 'Direction';
  return 'Site';
}

function getSiteTypeColor(type) {
  if (type === 'store') return 'primary';
  if (type === 'workshop') return 'secondary';
  if (type === 'sav') return 'info';
  if (type === 'direction') return 'warning';
  return 'default';
}

function matchesFilter(value, filter) {
  return String(value || '').toLowerCase().includes(filter.trim().toLowerCase());
}

function compareSortValues(left, right) {
  if (typeof left === 'boolean' || typeof right === 'boolean') {
    return Number(Boolean(left)) - Number(Boolean(right));
  }
  return String(left || '').localeCompare(String(right || ''), 'fr', { numeric: true, sensitivity: 'base' });
}

function toApiPayload(form) {
  const payload = stripEmptyValues({
    code: form.code,
    identificationNumber: form.identificationNumber,
    externalReference: form.externalReference,
    name: form.name,
    type: form.type,
    jobNumberPattern: form.type === 'store' ? form.jobNumberPattern : '',
    invoiceNumberPattern: form.type === 'store' ? form.invoiceNumberPattern : '',
    address: {
      address1: form.address1,
      address2: form.address2,
      postalCode: form.postalCode,
      city: form.city,
      state: form.state,
      country: form.country,
    },
    contact: {
      phone: form.phone,
      email: form.email,
    },
    supplierAccounts:
      form.type === 'store' && form.supplierAccountNumber
        ? [{ supplier: form.supplierAccountSupplier, accountNumber: form.supplierAccountNumber }]
        : [],
    authPassword: form.authPassword,
  });

  payload.logoUrl = form.logoUrl || '';
  payload.storeType = form.type === 'store' ? form.storeType || '' : '';
  payload.jobNumberPattern = form.type === 'store' ? form.jobNumberPattern || '' : '';
  payload.invoiceNumberPattern = form.type === 'store' ? form.invoiceNumberPattern || '' : '';
  return payload;
}

function fromApiItem(item) {
  const primarySupplierAccount = getPrimarySupplierAccount(item);

  return {
    code: item.code || '',
    identificationNumber: item.identificationNumber || '',
    externalReference: item.externalReference || '',
    logoUrl: item.logoUrl || '',
    name: item.name || '',
    type: item.type || 'store',
    storeType: item.storeType || '',
    jobNumberPattern: item.jobNumberPattern || '',
    invoiceNumberPattern: item.invoiceNumberPattern || '',
    address1: item.address?.address1 || '',
    address2: item.address?.address2 || '',
    postalCode: item.address?.postalCode || '',
    city: item.address?.city || '',
    state: item.address?.state || '',
    country: item.address?.country || 'France',
    phone: item.contact?.phone || '',
    email: item.contact?.email || '',
    supplierAccountSupplier: primarySupplierAccount?.supplier || 'BBGR',
    supplierAccountNumber: primarySupplierAccount?.accountNumber || '',
    authPassword: '',
  };
}

export default function SitesPage() {
  const [searchParams] = useSearchParams();
  const menuPage = useMenuPage('/sites');
  const [rows, setRows] = useState([]);
  const [storeTypes, setStoreTypes] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [open, setOpen] = useState(false);
  const [showAuthPassword, setShowAuthPassword] = useState(false);
  const [search, setSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState({
    site: '',
    name: '',
    type: '',
    storeType: '',
    city: '',
    country: '',
    supplier: '',
    accountNumber: '',
  });
  const [sortConfig, setSortConfig] = useState({ key: 'site', direction: 'asc' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const payload = await requestJson('/sites');
      setRows(getCollection(payload));
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    const q = searchParams.get('q') || '';
    if (q) setSearch(q);
  }, [searchParams]);

  const loadSettings = useCallback(async () => {
    try {
      const payload = await requestJson('/settings/SITES');
      setStoreTypes(payload.value?.storeTypes || ['OPTIQUE DE BOURBON', 'ALAIN AFFLELOU']);
    } catch (_err) {
      setStoreTypes(['OPTIQUE DE BOURBON', 'ALAIN AFFLELOU']);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const filterOptions = useMemo(() => {
    const collect = (selector) => [...new Set(rows.map(selector).filter(Boolean))].sort();

    return {
      sites: collect((row) => row.code),
      names: collect((row) => row.name),
      types: collect((row) => getSiteTypeLabel(row.type)),
      storeTypes: collect((row) => row.storeType),
      cities: collect((row) => row.address?.city),
      countries: collect((row) => row.address?.country),
      suppliers: collect((row) => getPrimarySupplierAccount(row)?.supplier),
      accountNumbers: collect((row) => getPrimarySupplierAccount(row)?.accountNumber),
    };
  }, [rows]);

  const tableColumns = useMemo(
    () => [
      { key: 'site', label: 'Site', getValue: (row) => row.code },
      { key: 'name', label: 'Nom', getValue: (row) => row.name },
      { key: 'type', label: 'Type', getValue: (row) => getSiteTypeLabel(row.type) },
      { key: 'storeType', label: 'Type de magasin', getValue: (row) => (row.type === 'store' ? row.storeType : '') },
      { key: 'city', label: 'Ville', getValue: (row) => row.address?.city },
      { key: 'country', label: 'Pays', getValue: (row) => row.address?.country },
      { key: 'supplier', label: 'Compte fournisseur', getValue: (row) => getPrimarySupplierAccount(row)?.supplier },
      { key: 'accountNumber', label: 'Numéro de compte', getValue: (row) => getPrimarySupplierAccount(row)?.accountNumber },
      { key: 'password', label: 'Mot de passe', getValue: (row) => Boolean(row.hasAuthPassword) },
    ],
    []
  );

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const filteredRows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return rows.filter((row) =>
      (!normalizedSearch ||
        [
          row.code,
          row.identificationNumber,
          row.name,
          getSiteTypeLabel(row.type),
          row.storeType,
          row.address?.city,
          row.address?.country,
          getPrimarySupplierAccount(row)?.supplier,
          getPrimarySupplierAccount(row)?.accountNumber,
        ]
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearch)) &&
      matchesFilter(row.code, columnFilters.site) &&
      matchesFilter(row.name, columnFilters.name) &&
      matchesFilter(getSiteTypeLabel(row.type), columnFilters.type) &&
      matchesFilter(row.storeType, columnFilters.storeType) &&
      matchesFilter(row.address?.city, columnFilters.city) &&
      matchesFilter(row.address?.country, columnFilters.country) &&
      matchesFilter(getPrimarySupplierAccount(row)?.supplier, columnFilters.supplier) &&
      matchesFilter(getPrimarySupplierAccount(row)?.accountNumber, columnFilters.accountNumber)
    );
  }, [rows, search, columnFilters]);

  const sortedRows = useMemo(() => {
    const column = tableColumns.find((item) => item.key === sortConfig.key) || tableColumns[0];
    const directionMultiplier = sortConfig.direction === 'asc' ? 1 : -1;
    return [...filteredRows].sort((left, right) =>
      compareSortValues(column.getValue(left), column.getValue(right)) * directionMultiplier
    );
  }, [filteredRows, sortConfig, tableColumns]);

  const resetFilters = () => {
    setSearch('');
    setColumnFilters({
      site: '',
      name: '',
      type: '',
      storeType: '',
      city: '',
      country: '',
      supplier: '',
      accountNumber: '',
    });
  };

  const filterFields = [
    ['site', 'Site', filterOptions.sites],
    ['name', 'Nom', filterOptions.names],
    ['type', 'Type', filterOptions.types],
    ['storeType', 'Type de magasin', filterOptions.storeTypes],
    ['city', 'Ville', filterOptions.cities],
    ['country', 'Pays', filterOptions.countries],
    ['supplier', 'Compte fournisseur', filterOptions.suppliers],
    ['accountNumber', 'Numéro de compte', filterOptions.accountNumbers],
  ];

  const handleOpenCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowAuthPassword(false);
    setOpen(true);
  };

  const handleOpenEdit = (row) => {
    setForm(fromApiItem(row));
    setEditingId(row._id);
    setShowAuthPassword(false);
    setOpen(true);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value,
      ...(name === 'type' && value !== 'store' ? { storeType: '' } : {}),
    }));
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Le logo doit être une image.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setForm((current) => ({ ...current, logoUrl: reader.result || '' }));
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError('');
      await requestJson(editingId ? `/sites/${editingId}` : '/sites', {
        method: editingId ? 'PUT' : 'POST',
        body: JSON.stringify(toApiPayload(form)),
      });
      setOpen(false);
      await loadRows();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await requestJson(`/sites/${id}`, { method: 'DELETE' });
      setDeleteTarget(null);
      await loadRows();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>
          {menuPage.sectionTitle}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 900 }}>
          {menuPage.pageTitle}
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', borderRadius: 2 }}>
        <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
          <Stack spacing={1.25}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FilterAltRoundedIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                  Filtres
                </Typography>
              </Stack>
              <Button size="small" variant="outlined" onClick={resetFilters} sx={{ minHeight: 32 }}>
                Réinitialiser
              </Button>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(auto-fit, minmax(150px, 1fr))',
                },
                gap: 1.5,
              }}
            >
              <TextField
                size="small"
                label="Recherche globale"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />
              {filterFields.map(([key, label, options]) => (
                <Autocomplete
                  key={key}
                  freeSolo
                  size="small"
                  options={options}
                  value={columnFilters[key] || ''}
                  onInputChange={(_event, value) =>
                    setColumnFilters((current) => ({ ...current, [key]: value || '' }))
                  }
                  onChange={(_event, value) =>
                    setColumnFilters((current) => ({ ...current, [key]: value || '' }))
                  }
                  renderInput={(params) => <TextField {...params} label={label} />}
                  fullWidth
                />
              ))}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ alignSelf: 'center' }}>
              {filteredRows.length} site{filteredRows.length > 1 ? 's' : ''}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadRows}>Rafraîchir</Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>Ajouter</Button>
            </Stack>
          </Stack>

          {loading ? (
            <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {tableColumns.map((column) => (
                      <TableCell
                        key={column.key}
                        sortDirection={sortConfig.key === column.key ? sortConfig.direction : false}
                      >
                        <TableSortLabel
                          active={sortConfig.key === column.key}
                          direction={sortConfig.key === column.key ? sortConfig.direction : 'asc'}
                          onClick={() => handleSort(column.key)}
                        >
                          {column.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRows.map((row) => {
                    const primarySupplierAccount = getPrimarySupplierAccount(row);

                    return (
                      <TableRow key={row._id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1.2} alignItems="center">
                            <Avatar
                              src={row.logoUrl || undefined}
                              alt={row.name}
                              sx={{
                                width: 34,
                                height: 34,
                                bgcolor:
                                  row.type === 'store'
                                    ? 'primary.main'
                                    : row.type === 'sav'
                                      ? 'info.main'
                                      : row.type === 'direction'
                                        ? 'warning.main'
                                        : 'secondary.main',
                                fontSize: 12,
                                fontWeight: 900,
                              }}
                            >
                              {row.code?.slice(0, 3) || '?'}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 900 }}>
                              {row.code}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>{row.name}</TableCell>
                        <TableCell><Chip size="small" label={getSiteTypeLabel(row.type)} color={getSiteTypeColor(row.type)} variant="outlined" /></TableCell>
                        <TableCell>{row.type === 'store' ? row.storeType || '—' : '—'}</TableCell>
                        <TableCell>{row.address?.city || '—'}</TableCell>
                        <TableCell>{row.address?.country || '—'}</TableCell>
                        <TableCell>{primarySupplierAccount?.supplier || '—'}</TableCell>
                        <TableCell>{primarySupplierAccount?.accountNumber || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.hasAuthPassword ? 'Défini' : 'À définir'}
                            color={row.hasAuthPassword ? 'success' : 'warning'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleOpenEdit(row)}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => setDeleteTarget(row)}><DeleteIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => { setShowAuthPassword(false); setOpen(false); }} fullWidth maxWidth="md">
        <Box component="form" onSubmit={handleSubmit}>
          <DialogTitle>{editingId ? 'Modifier un site' : 'Ajouter un site'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField name="code" label="Code site" value={form.code} onChange={handleChange} required fullWidth />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField name="name" label="Nom" value={form.name} onChange={handleChange} required fullWidth />
                <TextField name="type" label="Type" select value={form.type} onChange={handleChange} fullWidth>
                  <MenuItem value="store">Magasin</MenuItem>
                  <MenuItem value="workshop">Atelier</MenuItem>
                  <MenuItem value="sav">Logistique</MenuItem>
                  <MenuItem value="direction">Direction</MenuItem>
                </TextField>
              </Stack>
              <TextField
                name="authPassword"
                label={editingId ? 'Nouveau mot de passe d’accès' : 'Mot de passe d’accès'}
                type={showAuthPassword ? 'text' : 'password'}
                value={form.authPassword}
                onChange={handleChange}
                helperText={editingId ? 'Laisse vide pour conserver le mot de passe actuel.' : 'Utilisé pour se connecter à ce site.'}
                required={!editingId}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Maintenir pour afficher le mot de passe">
                        <IconButton
                          aria-label="Afficher temporairement le mot de passe"
                          edge="end"
                          onMouseDown={(event) => event.preventDefault()}
                          onPointerDown={() => setShowAuthPassword(true)}
                          onPointerUp={() => setShowAuthPassword(false)}
                          onPointerLeave={() => setShowAuthPassword(false)}
                          onPointerCancel={() => setShowAuthPassword(false)}
                        >
                          {showAuthPassword ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
              {form.type === 'store' && (
                <Stack spacing={2}>
                  <TextField
                    name="storeType"
                    label="Type de magasin"
                    select
                    value={form.storeType}
                    onChange={handleChange}
                    fullWidth
                  >
                    <MenuItem value="">Non renseigné</MenuItem>
                    {storeTypes.map((storeType) => (
                      <MenuItem key={storeType} value={storeType}>
                        {storeType}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    name="jobNumberPattern"
                    label="Pattern numéros de job"
                    value={form.jobNumberPattern}
                    onChange={handleChange}
                    helperText="Optionnel. Regex appliquée aux bips magasin du suivi atelier. Exemple AA22 : ^122[A-Z0-9]{3}$"
                    placeholder="Ex: ^122[A-Z0-9]{3}$"
                    fullWidth
                  />
                  <TextField
                    name="invoiceNumberPattern"
                    label="Pattern numéros de facture"
                    value={form.invoiceNumberPattern}
                    onChange={handleChange}
                    helperText="Regex utilisée pour RETAILLAGE et CLIP. Exemple AA21 : ^121[0-9]{6}$"
                    placeholder="Ex: ^121[0-9]{6}$"
                    fullWidth
                  />
                </Stack>
              )}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <Avatar
                  src={form.logoUrl || undefined}
                  alt={form.name || form.code}
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: form.type === 'store' ? 'primary.main' : form.type === 'sav' ? 'info.main' : 'secondary.main',
                    fontWeight: 900,
                  }}
                >
                  {form.code?.slice(0, 3) || '?'}
                </Avatar>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 1 }}>
                  <Button variant="contained" component="label" startIcon={<PhotoCameraIcon />}>
                    Uploader le logo
                    <input type="file" accept="image/*" hidden onChange={handleLogoUpload} />
                  </Button>
                  {form.logoUrl && (
                    <Button variant="text" color="error" onClick={() => setForm((current) => ({ ...current, logoUrl: '' }))}>
                      Retirer
                    </Button>
                  )}
                </Stack>
              </Stack>
              <TextField name="address1" label="Adresse 1" value={form.address1} onChange={handleChange} />
              <TextField name="address2" label="Adresse 2" value={form.address2} onChange={handleChange} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField name="postalCode" label="Code postal" value={form.postalCode} onChange={handleChange} fullWidth />
                <TextField name="city" label="Ville" value={form.city} onChange={handleChange} fullWidth />
                <TextField name="country" label="Pays" value={form.country} onChange={handleChange} fullWidth />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField name="phone" label="Téléphone" value={form.phone} onChange={handleChange} fullWidth />
                <TextField name="email" label="Email" type="email" value={form.email} onChange={handleChange} fullWidth />
              </Stack>
              {form.type === 'store' && (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Compte fournisseur magasin
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      name="supplierAccountSupplier"
                      label="Fournisseur"
                      select
                      value={form.supplierAccountSupplier}
                      onChange={handleChange}
                      fullWidth
                    >
                      <MenuItem value="BBGR">BBGR</MenuItem>
                      <MenuItem value="ESSILOR">ESSILOR</MenuItem>
                    </TextField>
                    <TextField
                      name="supplierAccountNumber"
                      label="Numéro de compte"
                      value={form.supplierAccountNumber}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Stack>
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Annuler</Button>
            <Button type="submit" variant="contained" disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</Button>
          </DialogActions>
        </Box>
      </Dialog>

      <ConfirmDeleteDialog
        open={Boolean(deleteTarget)}
        itemLabel={`le site ${deleteTarget?.code || deleteTarget?.name || ''}`}
        description={`Confirmer la suppression du site ${
          deleteTarget?.code || deleteTarget?.name || ''
        } ?`}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => handleDelete(deleteTarget._id)}
      />
    </Stack>
  );
}
